import { useMemo, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { TicketListItem } from "~/types/common";
import { Dialog } from "@/Dialog";

interface SubscriptionPlan {
  planSeq: number;
  planName: string;
  contents: string;
  price: number;
  discountRate: number;
  discountedPrice: number;
  badges: string[];
  productTicketSeq?: number; // 이용권 매칭을 위한 필드
  cdProductType?: string;
}

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  PRODUCT_TYPE_KINDERGARTEN: "유치원",
  PRODUCT_TYPE_DAYCARE: "데이케어",
  PRODUCT_TYPE_HOTEL: "호텔",
  PRODUCT_TYPE_GROOMING: "미용",
};

// CompanyDetailType의 tickets 타입
interface CompanyDetailTicket {
  purchaseTicketSeq: number;
  productTicketSeq: number;
  userSeq: number;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  qty: number;
  badge: string[];
  companyName: string;
  address: string;
  companySeq: number;
  purchaseDate: string;
  expiredDate: string;
  cdProductType: string;
}

interface SubscriptionPlansSectionProps {
  companySeq?: number;
  companyName?: string;
  tickets?: CompanyDetailTicket[];
  ticketList?: TicketListItem[];
  isTicketListLoading?: boolean;
  isServiceActive?: boolean;
}

export const SubscriptionPlansSection = ({
  companySeq,
  companyName,
  tickets,
  ticketList = [],
  isTicketListLoading = false,
  isServiceActive = true,
}: SubscriptionPlansSectionProps) => {
  const { history } = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveModalMessage, setInactiveModalMessage] = useState(
    "매장에서 서비스를 시작하지 않아 이용권을 결제할 수 없습니다. 매장에 문의하여 주세요.",
  );

  // 각 이용권의 보유 개수를 계산하는 함수
  const getTicketCount = (productTicketSeq?: number): number => {
    if (!productTicketSeq || !ticketList || ticketList.length === 0) {
      return 0;
    }
    return ticketList.filter(
      (ticket) => ticket.productTicketSeq === productTicketSeq,
    ).length;
  };

  // API에서 받은 tickets 데이터를 SubscriptionPlan 형식으로 변환
  const plans: SubscriptionPlan[] = tickets
    ? tickets.map((ticket, index) => ({
        planSeq: index + 1,
        planName: ticket.name,
        contents: ticket.contents,
        price: ticket.cost,
        discountRate: ticket.discount,
        discountedPrice: ticket.amount,
        badges: ticket.badge || [],
        productTicketSeq: ticket.productTicketSeq,
        cdProductType: ticket.cdProductType,
      }))
    : [];

  // 보유 상품군 (탭 노출용). 등장 순서를 유지.
  const productTypeTabs = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    plans.forEach((p) => {
      const t = p.cdProductType;
      if (t && !seen.has(t)) {
        seen.add(t);
        list.push(t);
      }
    });
    return list;
  }, [plans]);

  const [activeProductType, setActiveProductType] = useState<string | null>(
    null,
  );
  // 첫 진입 또는 데이터 변경 시 기본 탭 보정
  const effectiveActiveType =
    activeProductType && productTypeTabs.includes(activeProductType)
      ? activeProductType
      : (productTypeTabs[0] ?? null);

  const filteredPlans =
    productTypeTabs.length > 1 && effectiveActiveType
      ? plans.filter((p) => p.cdProductType === effectiveActiveType)
      : plans;

  // 이용권 리스트가 없으면 아무것도 표시하지 않음
  if (!plans || plans.length === 0) {
    return null;
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const hasMorePlans = filteredPlans.length > 3;
  const displayPlans = isExpanded
    ? filteredPlans
    : filteredPlans.slice(0, 3);

  return (
    <div className="px-4 mb-3">
      <div className="p-4 bg-white rounded-xl shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2 items-center">
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-[17px] font-semibold text-gray-800">
              {companyName ? `${companyName} 제공 서비스` : "이용권"}
            </h3>
          </div>
        </div>
        {productTypeTabs.length > 1 && (
          <div className="flex gap-1 mb-3 -mx-1 px-1 overflow-x-auto scrollbar-hide">
            {productTypeTabs.map((type) => {
              const isActive = effectiveActiveType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setActiveProductType(type);
                    setIsExpanded(false);
                  }}
                  className={
                    "flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors " +
                    (isActive
                      ? "bg-orange-400 text-white border-orange-400"
                      : "bg-white text-gray-600 border-gray-200 hover:border-orange-300")
                  }
                >
                  {PRODUCT_TYPE_LABELS[type] ?? type}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-col gap-3">
          {displayPlans.map((plan) => {
            const ticketCount = getTicketCount(plan.productTicketSeq);
            console.log(ticketCount);
            return (
              <div
                key={plan.planSeq}
                className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <div className="flex gap-2 justify-between items-start mb-1">
                    <span className="flex-1 text-sm font-semibold text-gray-800 break-words">
                      {plan.planName}
                    </span>
                    {!isTicketListLoading && ticketCount > 0 && (
                      <span className="flex-shrink-0 text-xs font-semibold text-[#FF0000] whitespace-nowrap">
                        {ticketCount}개 남음
                      </span>
                    )}
                  </div>
                  {plan.contents && (
                    <p className="text-xs text-[#959595] mb-1 break-words">
                      {plan.contents}
                    </p>
                  )}
                  <div className="flex gap-2 items-center mb-1 min-h-[23px]">
                    {plan.badges.length > 0 && (
                      <>
                        {plan.badges.map((badge, idx) => (
                          <span
                            key={idx}
                            className="px-2 h-[23px] text-xs font-medium text-[#111111] bg-[#EBEBEB] rounded-full flex items-center"
                          >
                            {badge}
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 justify-between items-center">
                    <div className="flex gap-1 items-center">
                      {plan.discountRate > 0 && (
                        <span className="text-xs text-[#959595] line-through">
                          {formatPrice(plan.price)}원
                        </span>
                      )}
                      <span className="text-sm font-semibold text-[#111111]">
                        {formatPrice(plan.discountedPrice)}원
                      </span>
                      {plan.discountRate > 0 && (
                        <span className="text-xs font-medium text-[#FF0000] ml-1">
                          {plan.discountRate}%
                        </span>
                      )}
                    </div>
                    {!isTicketListLoading && (
                      <button
                        className={`flex-shrink-0 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors bg-orange-400 hover:bg-orange-500`}
                        onClick={() => {
                          // 서비스 미운영 매장은 결제 불가 모달 노출
                          if (!isServiceActive) {
                            setInactiveModalMessage(
                              "매장에서 서비스를 시작하지 않아 이용권을 결제할 수 없습니다. 매장에 문의하여 주세요.",
                            );
                            setShowInactiveModal(true);
                            return;
                          }
                          // 일괄적으로 구매하기 페이지로 이동
                          history.push(
                            `/customer/layout/subscription-list?companySeq=${companySeq || ""}`,
                          );
                        }}
                      >
                        구매하기
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {hasMorePlans && (
          <div className="flex justify-center mt-2">
            <button
              className="flex justify-center items-center py-1 text-gray-400 transition-colors hover:text-gray-600"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* 서비스 미운영 상태 모달 */}
      <Dialog
        visible={showInactiveModal}
        onClose={() => setShowInactiveModal(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">알림</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {inactiveModalMessage}
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => setShowInactiveModal(false)}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
